import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Divider,
    InputAdornment,
} from "@mui/material";
import {
    CalendarMonthOutlined,
    CategoryOutlined,
    ExtensionOutlined,
    DownloadOutlined,
    TuneOutlined,
} from "@mui/icons-material";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";

const COLORS = {
    primary: "#003049",
    primaryAlt: "#045182",
    primaryGradient: "linear-gradient(135deg, #003049, #045182)",
    textMain: "#111827",
    textSubtle: "#6b7280",
    cardBorder: "rgba(0, 48, 73, 0.18)",
    fieldBg: "linear-gradient(180deg, #ffffff, #f9fafb)",
};

const OnlineSalesReport = ({ open, onClose }) => {
    const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");
    const token = localStorage.getItem("token");

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [platform, setPlatform] = useState('');
    const [categories, setCategories] = useState([]);

    const headers = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
        }),
        [token]
    );

    const resetForm = () => {
        setStartDate("");
        setEndDate("");
        setPlatform("");
        setSelectedCategory("");
    };

    const handleClose = () => {
        if (downloading) return;
        onClose?.();
    };

    const formatDate = (date) => {
        if (!date) return "";
        return new Date(date).toISOString().split("T")[0];
    };

    const handleDownload = async () => {
        if (!outletData?._id || !startDate || !endDate) {
            toast.error("Please select start date and end date", { autoClose: 3000 });
            return;
        }

        if (new Date(startDate) > new Date(endDate)) {
            toast.error("Start date cannot be greater than end date", {
                autoClose: 3000,
            });
            return;
        }

        setDownloading(true);

        try {
            const params = {
                isDownload: true,
                isCosting: false,
                outlet: outletData._id,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
            };

            if (selectedCategory) params.category = selectedCategory;
            if (platform) params.platform = platform;

            const response = await axios.get(
                `${API.BASE_URL}third-party-sales`,
                {
                    headers,
                    params,
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            const fileName = `online_sales_report_${formatDate(
                startDate
            )}_to_${formatDate(endDate)}.xlsx`;

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("Report downloaded successfully", { autoClose: 2500 });
            resetForm();
            onClose?.();
        } catch (error) {
            console.error("Error downloading report:", error);
            toast.error("Failed to download report. Please try again.", {
                autoClose: 3000,
            });
        } finally {
            setDownloading(false);
        }
    };

    const getCategories = async () => {
        try {
            const response = await axios.get(`${API.BASE_URL}masters/category/get`, {
                headers,
            });

            const cats = response?.data?.data?.categories || [];
            setCategories(Array.isArray(cats) ? cats : []);
        } catch (error) {
            console.error("Error fetching categories:", error);
            toast.error("Failed to fetch categories", { autoClose: 3000 });
        }
    };

    useEffect(() => {
        if (!open || !token) return;
        getCategories();
    }, [open, token]);

    const commonFieldSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            background: COLORS.fieldBg,
            fontSize: "0.95rem",
            "& fieldset": {
                borderColor: COLORS.cardBorder,
            },
            "&:hover fieldset": {
                borderColor: COLORS.primaryAlt,
            },
            "&.Mui-focused fieldset": {
                borderColor: COLORS.primary,
                borderWidth: "1px",
            },
        },
        "& .MuiInputLabel-root": {
            color: COLORS.textSubtle,
            fontWeight: 600,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: COLORS.primary,
        },
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: "hidden",
                    background:
                        "linear-gradient(180deg, #ffffff 0%, #fbfdff 50%, #f6f9fc 100%)",
                    boxShadow: "0 24px 60px rgba(0, 48, 73, 0.16)",
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: 3,
                    py: 2.25,
                    background:
                        "linear-gradient(135deg, rgba(0,48,73,1) 0%, rgba(4,81,130,1) 100%)",
                    color: "#fff",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.2}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255,255,255,0.14)",
                            border: "1px solid rgba(255,255,255,0.18)",
                        }}
                    >
                        <DownloadOutlined sx={{ fontSize: 22 }} />
                    </Box>

                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.2 }}>
                            Download Online Sales Report
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2.5 }}>
                <Box
                    sx={{
                        mt: 2,
                        mb: 2,
                        p: 1.6,
                        borderRadius: 3,
                        border: `1px solid ${COLORS.cardBorder}`,
                        background: "linear-gradient(135deg, rgba(214,227,243,0.45), rgba(255,255,255,0.95))",
                    }}
                >
                    <Typography
                        sx={{
                            color: COLORS.textMain,
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            mb: 0.5,
                        }}
                    >
                        Report Filters
                    </Typography>
                    <Typography
                        sx={{
                            color: COLORS.textSubtle,
                            fontSize: "0.8rem",
                            lineHeight: 1.5,
                        }}
                    >
                        Date range is mandatory. Other fields are optional and help compact the report.
                    </Typography>
                </Box>

                <Stack spacing={2}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={commonFieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonthOutlined sx={{ color: COLORS.primary, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={commonFieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonthOutlined sx={{ color: COLORS.primary, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>

                    <Divider sx={{ borderColor: "rgba(0, 48, 73, 0.10)" }} />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <TextField
                            select
                            label="Category"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            fullWidth
                            sx={commonFieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CategoryOutlined sx={{ color: COLORS.primary, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((item) => (
                                <MenuItem key={item._id} value={item._id}>
                                    {item.title || item.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Platform"
                            value={platform}
                            onChange={(e) => setPlatform(e.target.value)}
                            fullWidth
                            sx={commonFieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <ExtensionOutlined sx={{ color: COLORS.primary, fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            <MenuItem value="">All Platform</MenuItem>
                            <MenuItem value="ZOMATO">ZOMATO</MenuItem>
                            <MenuItem value="SWIGGY">SWIGGY</MenuItem>
                        </TextField>
                    </Stack>

                    {downloading && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                mt: 0.5,
                                px: 1.5,
                                py: 1.2,
                                borderRadius: 3,
                                background:
                                    "linear-gradient(135deg, rgba(214,227,243,0.45), rgba(255,255,255,1))",
                                border: `1px solid ${COLORS.cardBorder}`,
                            }}
                        >
                            <CircularProgress size={20} sx={{ color: COLORS.primary }} />
                            <Typography
                                variant="body2"
                                sx={{ color: COLORS.textMain, fontWeight: 600 }}
                            >
                                Preparing report...
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: "1px solid rgba(0, 48, 73, 0.08)",
                    background: "linear-gradient(180deg, #ffffff, #f8fafc)",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.2,
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={downloading}
                    sx={{
                        px: 2.2,
                        py: 1,
                        borderRadius: 2.5,
                        fontWeight: 700,
                        color: COLORS.textMain,
                        border: `1px solid ${COLORS.cardBorder}`,
                        background: "#fff",
                        textTransform: "none",
                        "&:hover": {
                            background: "#f9fafb",
                            borderColor: COLORS.primaryAlt,
                        },
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={downloading}
                    startIcon={<DownloadOutlined />}
                    sx={{
                        px: 2.2,
                        py: 1,
                        borderRadius: 2.5,
                        fontWeight: 700,
                        textTransform: "none",
                        background: COLORS.primaryGradient,
                        boxShadow: "0 10px 24px rgba(0, 48, 73, 0.22)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #00263a, #03466f)",
                            boxShadow: "0 12px 28px rgba(0, 48, 73, 0.28)",
                        },
                    }}
                >
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OnlineSalesReport;